import { Component, inject, EventEmitter, HostListener, OnDestroy, OnInit, Output } from '@angular/core';
import { User } from '../../../../../_shared/models';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PaginatedReconciledFuelRequest, ReconciledFuel, CurrencyOption, UpdateReconciledFuelSlipRequest } from '../../../models/fuel-recon.model';
import { CountryFlagComponent } from '../../../../../_shared/components/country-flag/country-flag.component';
import { Subject, takeUntil } from 'rxjs';
import { FuelReconciliationService } from '../../../services/fuel-reconciliation.service';
import { ToastrService } from 'ngx-toastr';
import { isEmptyObject } from '../../../../../_shared/helpers/object.helper';
import { AuthService, FilterService } from '../../../../../_shared/services';
import { PaginationComponent } from '../../../../../_shared/components/pagination/pagination.component';
import { toSeconds } from '../../../helpers/timezone.helper';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../../../_shared/components/states/error-state/error-state.component';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AttachmentViewModalComponent } from '../../../../../_shared/components/modals/attachment-view-modal/attachment-view-modal.component';
import { ConfirmationModalService } from '../../../../../_shared/services/modals/confirmation-modal.service';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-reconciled-fuel-slips-table',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, EmptyStateComponent, ErrorStateComponent, CountryFlagComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './reconciled-fuel-slips-table.component.html',
  styleUrl: './reconciled-fuel-slips-table.component.scss',
})
export class ReconciledFuelSlipsTableComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  public reconciledFuel: ReconciledFuel[];
  public isLoading: boolean = false;
  public totalItems: number = 0;
  public currentItems: number = 0;
  public currentPage: number = 1;
  public numberOfPages: number = 0;
  public hasError: boolean = false;
  public errorMessage: string;
  private destroySubject: Subject<void> = new Subject<void>();
  private paginatedRequest: PaginatedReconciledFuelRequest;
  private cancellation: Subject<void> = new Subject<void>();
  private user: User;
  private sortColumn: keyof ReconciledFuel | null = null;
  private sortDirection: 'asc' | 'desc' = 'asc';
  public currencyCode: string = '';
  public currencySymbol: string = '';
  @Output() slipDeleted = new EventEmitter<void>();
  public openMenuId: number | null = null;
  public dropdownPos: { bottom: number; right: number } = { bottom: 0, right: 0 };

  // Inline edit state
  public expandedRowIndex: number | null = null;
  public editAmount!: number;
  public editCost!: number;
  public editCostType: string | null = null;
  public editSelectedFile: File | null = null;
  public currencies: CurrencyOption[] = [];
  public filteredCurrencies: CurrencyOption[] = [];
  public editSelectedCurrency: CurrencyOption | null = null;
  public editCurrencySearchTerm: string = '';
  public isEditLoading: boolean = false;
  private originalEditAmount!: number;
  private originalEditCost!: number;
  private originalEditCostType!: string;
  private originalEditCurrencyId!: number;
  private currencyOptionsCancelSubject: Subject<void> = new Subject<void>();
  private previewBlobUrl: string | null = null;

  constructor(
    private fuelReconService: FuelReconciliationService,
    private toastr: ToastrService,
    private authService: AuthService,
    private errorService: ErrorHandlerService,
    private filterService: FilterService,
    private modalService: NgbModal,
    private confirmationModalService: ConfirmationModalService
  ) {
    this.user = this.authService.getUser();
    this.paginatedRequest = {
      pageSize: 10,
      pageNumber: 1,
      accountId: this.user.accountId,
      utcOffset: 0,
      startDate: new Date(),
      endDate: new Date(),
      fuelIntake: '',
    };

    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  ngOnInit(): void {
    this.loadCurrenciesAsync();
    this.filterService.filterUpdated$
      .pipe(takeUntil(this.destroySubject))
      .subscribe(async (filter) => {
        if (!isEmptyObject(filter)) {
          this.paginatedRequest.startDate = filter.startDate;
          this.paginatedRequest.endDate = filter.endDate;
          await this.getPaginatedReconciledFuelByAccountIdAsync();
        }
      });

    this.filterService.searchTerm$
      .pipe(takeUntil(this.destroySubject))
      .subscribe(async (searchTerm) => {
        this.paginatedRequest.fuelIntake = searchTerm;
        this.paginatedRequest.pageNumber = 1;
        this.currentPage = 1;
        await this.getPaginatedReconciledFuelByAccountIdAsync();
      });
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
    this.currencyOptionsCancelSubject.next();
    this.currencyOptionsCancelSubject.complete();
    if (this.previewBlobUrl) URL.revokeObjectURL(this.previewBlobUrl);
  }

  public async createPagination(page: number): Promise<void> {
    if (page > this.numberOfPages || page < 1) {
      return;
    }

    this.paginatedRequest.pageNumber = page;
    this.currentPage = page;

    await this.getPaginatedReconciledFuelByAccountIdAsync();
  }

  public async getPaginatedReconciledFuelByAccountIdAsync(): Promise<void> {
    try {
      this.cancellation.next();
      this.reconciledFuel = [];
      this.hasError = false;
      this.isLoading = true;
      this.getUtcOffsetFromFuelSlipUploadPreference();
      const response = await this.fuelReconService.getPaginatedReconciledFuelByAccountIdAsync(
        this.paginatedRequest,
        this.cancellation
      );
      this.reconciledFuel = response.data?.results ?? [];
      this.totalItems = response.data?.totalCount ?? 0;
      this.currentItems = Math.min(
        this.currentPage * this.paginatedRequest.pageSize,
        this.totalItems
      );
      this.numberOfPages = response.data?.totalPages ?? 0;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: (message) => {
          this.errorMessage = message;
          this.hasError = true;
        }
      });
    } finally {
      this.isLoading = false;
    }
  }

  public sortByColumn(column: keyof ReconciledFuel): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.reconciledFuel = [...this.reconciledFuel].sort((a, b) => {
      const valueA = a[column];
      const valueB = b[column];

      if (valueA instanceof Date && valueB instanceof Date) {
        return this.sortDirection === 'asc'
          ? valueA.getTime() - valueB.getTime()
          : valueB.getTime() - valueA.getTime();
      }

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return this.sortDirection === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return this.sortDirection === 'asc'
          ? valueA - valueB
          : valueB - valueA;
      }

      return 0;
    });
  }

  private getUtcOffsetFromFuelSlipUploadPreference(): void {
    this.fuelReconService.getFuelSlipUploadPreferenceAsync(this.cancellation)
      .then(response => this.paginatedRequest.utcOffset = response.data?.utcOffset)
      .catch(() => this.paginatedRequest.utcOffset = -toSeconds(new Date().getTimezoneOffset()));
  }

  @HostListener('document:click')
  public closeMenu(): void {
    if (this.openMenuId === null) return;
    this.openMenuId = null;
  }

  public toggleMenu(index: number, event: MouseEvent): void {
    event.stopPropagation();
    if (this.openMenuId === index) {
      this.openMenuId = null;
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    this.dropdownPos = {
      bottom: window.innerHeight - rect.top + 4,
      right: window.innerWidth - rect.right
    };
    this.openMenuId = index;
  }

  public openEdit(slip: ReconciledFuel, index: number): void {
    this.openMenuId = null;
    this.expandedRowIndex = index;
    this.editAmount = Math.round(slip.fuelSlipAmount * 100) / 100;
    this.editCost = Math.round(slip.cost * 100) / 100;
    this.editCostType = slip.isTotalCost ? 'Total Cost' : 'Cost/ℓ';
    this.editSelectedFile = null;
    this.editCurrencySearchTerm = '';
    this.filteredCurrencies = [...this.currencies];

    if (this.currencies.length > 0) {
      this.editSelectedCurrency = this.currencies.find(c => c.id === slip.currencyId) ?? null;
    } else {
      this.loadCurrenciesAsync().then(() => {
        this.editSelectedCurrency = this.currencies.find(c => c.id === slip.currencyId) ?? null;
      });
    }

    this.originalEditAmount = Math.round(slip.fuelSlipAmount * 100) / 100;
    this.originalEditCost = Math.round(slip.cost * 100) / 100;
    this.originalEditCostType = this.editCostType;
    this.originalEditCurrencyId = slip.currencyId ?? 0;
  }

  public cancelEdit(): void {
    this.expandedRowIndex = null;
    this.editSelectedFile = null;
  }

  public get hasEditChanges(): boolean {
    return (
      this.editAmount !== this.originalEditAmount ||
      this.editCost !== this.originalEditCost ||
      this.editCostType !== this.originalEditCostType ||
      (this.editSelectedCurrency?.id ?? this.originalEditCurrencyId) !== this.originalEditCurrencyId ||
      this.editSelectedFile !== null
    );
  }

  public get isUpdateDisabled(): boolean {
    return (
      !this.hasEditChanges ||
      !this.editAmount || this.editAmount <= 0 ||
      !this.editCost || this.editCost <= 0 ||
      !this.editCostType ||
      !this.editSelectedCurrency ||
      this.isEditLoading
    );
  }

  public onEditFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.editSelectedFile = input.files?.[0] ?? null;
  }

  public removeEditFile(): void {
    this.editSelectedFile = null;
  }

  public async clearExistingAttachment(slip: ReconciledFuel): Promise<void> {
    await this.confirmationModalService.showConfirmationModal({
      title: this.transloco.translate('fuelReconciliation.messages.deleteAttachmentTitle'),
      message: this.transloco.translate('fuelReconciliation.messages.deleteAttachmentMessage'),
      warningMessage: this.transloco.translate('fuelReconciliation.messages.deleteAttachmentWarning'),
      confirmText: this.transloco.translate('fuelReconciliation.messages.yesDelete'),
      declineText: this.transloco.translate('fuelReconciliation.messages.noCancel'),
      onConfirm: async () => {
        await this.fuelReconService.deleteFuelSlipAttachmentAsync(slip.fuelSlipImportsId, this.cancellation);
        slip.originalFileName = undefined;
        this.toastr.success(this.transloco.translate('fuelReconciliation.messages.attachmentRemoved'), this.transloco.translate('fuelReconciliation.messages.success'));
      }
    });
  }

  public selectEditCostType(type: string): void {
    this.editCostType = type;
  }

  public filterEditCurrencies(): void {
    const term = this.editCurrencySearchTerm.toLowerCase();
    this.filteredCurrencies = this.currencies.filter(
      c => c.name.toLowerCase().includes(term) || c.code.toLowerCase().includes(term)
    );
  }

  public selectEditCurrency(currency: CurrencyOption): void {
    this.editSelectedCurrency = currency;
    this.editCurrencySearchTerm = '';
    this.filteredCurrencies = [...this.currencies];
  }

  public async updateReconciliationAsync(slip: ReconciledFuel): Promise<void> {
    if (this.isUpdateDisabled) return;
    try {
      this.isEditLoading = true;
      const request: UpdateReconciledFuelSlipRequest = {
        fuelSlipImportsId: slip.fuelSlipImportsId,
        amount: this.editAmount,
        cost: this.editCost,
        isTotalCost: this.editCostType === 'Total Cost',
        createdBy: this.user.login,
        currencyId: this.editSelectedCurrency!.id,
      };
      await this.fuelReconService.updateReconciledFuelSlipAsync(request, this.editSelectedFile, this.cancellation);
      this.toastr.success(this.transloco.translate('fuelReconciliation.messages.slipUpdated'), this.transloco.translate('fuelReconciliation.messages.success'));
      this.cancelEdit();
      await this.getPaginatedReconciledFuelByAccountIdAsync();
    } catch (error: any) {
      if (error.name !== 'EmptyError') {
        this.errorService.handleError(error, {
          callback: (message) => this.toastr.error(message)
        });
      }
    } finally {
      this.isEditLoading = false;
    }
  }

  private async loadCurrenciesAsync(): Promise<void> {
    try {
      const response = await this.fuelReconService.getCurrencyOptionsAsync(this.currencyOptionsCancelSubject);
      this.currencies = response.data ?? [];
      this.filteredCurrencies = [...this.currencies];
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: (message) => { this.errorMessage = message; }
      });
    }
  }

  public async openViewAttachment(slip: ReconciledFuel): Promise<void> {
    this.openMenuId = null;
    const modalRef = this.modalService.open(AttachmentViewModalComponent, { centered: true, size: 'lg' });
    modalRef.componentInstance.isLoading = true;
    modalRef.componentInstance.fileName = slip.originalFileName;

    try {
      const response = await this.fuelReconService.getFuelSlipAttachmentUrlAsync(slip.fuelSlipImportsId, this.cancellation);
      modalRef.componentInstance.fileUrl = response.data ?? null;
    } catch (error: any) {
      this.errorService.handleError(error);
    } finally {
      modalRef.componentInstance.isLoading = false;
    }
  }

  public previewEditFile(): void {
    if (!this.editSelectedFile) return;
    if (this.previewBlobUrl) URL.revokeObjectURL(this.previewBlobUrl);
    this.previewBlobUrl = URL.createObjectURL(this.editSelectedFile);
    const modalRef = this.modalService.open(AttachmentViewModalComponent, { centered: true, size: 'lg' });
    modalRef.componentInstance.fileUrl = this.previewBlobUrl;
    modalRef.componentInstance.fileType = this.editSelectedFile.type;
    modalRef.componentInstance.fileName = this.editSelectedFile.name;
    modalRef.result.finally(() => {
      if (this.previewBlobUrl) URL.revokeObjectURL(this.previewBlobUrl);
      this.previewBlobUrl = null;
    });
  }

  public async openDelete(slip: ReconciledFuel): Promise<void> {
    this.openMenuId = null;
    await this.confirmationModalService.showConfirmationModal({
      title: this.transloco.translate('fuelReconciliation.messages.removeTitle'),
      message: this.transloco.translate('fuelReconciliation.messages.removeMessage', { assetName: slip.assetName }),
      warningMessage: this.transloco.translate('fuelReconciliation.messages.removeWarning'),
      confirmText: this.transloco.translate('fuelReconciliation.messages.yesRemove'),
      declineText: this.transloco.translate('fuelReconciliation.messages.noCancel'),
      onConfirm: async () => {
        await this.fuelReconService.deleteReconciledFuelSlipAsync(slip.fuelSlipImportsId, this.cancellation);
        this.toastr.success(this.transloco.translate('fuelReconciliation.messages.slipRemoved'), this.transloco.translate('fuelReconciliation.messages.success'));
        await this.getPaginatedReconciledFuelByAccountIdAsync();
        this.slipDeleted.emit();
      }
    });
  }

  public isWithinTwoPercentMargin(fuelSlip: ReconciledFuel): boolean {
    const probeReading = fuelSlip.fuelProbeReading;
    const slipAmount = fuelSlip.fuelSlipAmount;

    if (!probeReading || !slipAmount) {
      return false;
    }

    const twoPercentMargin = probeReading * 0.02;
    const lowerBound = probeReading - twoPercentMargin;
    const upperBound = probeReading + twoPercentMargin;

    return slipAmount >= lowerBound && slipAmount <= upperBound;
  }
}
